"use client";

import { useEffect, useRef, useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { Select } from "@/components/ui/select";
import { Icon } from "@/components/ui/icon";
import { listJobs } from "@/lib/api/generated";
import { useInfiniteScroll } from "@/hooks/use-infinite-scroll";
import { useSelectedProject } from "@/hooks/use-search";
import type { Job } from "@/lib/api/types";

import { jobSelectionSchema, type JobSelection } from "../lib/job-selection";

export { jobSelectionSchema, type JobSelection };

const PER_PAGE = 20;

type JobInputProps = {
  value: JobSelection | null;
  onChange: (job: Job) => void;
  lastJobId?: string;
  placeholder?: string;
  hideChevron?: boolean;
} & Omit<
  React.ComponentProps<typeof Select.Trigger>,
  "value" | "onChange" | "children"
>;

export function JobInput({
  value,
  onChange,
  lastJobId,
  placeholder = "Select Job",
  hideChevron = false,
  className,
  variant,
  ...triggerProps
}: JobInputProps) {
  const { data: selectedProject } = useSelectedProject();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  // Latches true on 403 to break the invalidation loop in the global
  // handleForbidden QueryCache error handler. Resets when project changes.
  const [forbidden, setForbidden] = useState(false);

  const prevProjectIdRef = useRef(selectedProject?.id);
  useEffect(() => {
    if (prevProjectIdRef.current !== selectedProject?.id) {
      prevProjectIdRef.current = selectedProject?.id;
      setForbidden(false);
    }
  }, [selectedProject?.id]);

  const jobsQuery = useInfiniteQuery({
    queryKey: ["jobs", "input", selectedProject?.id, search.trim()],
    queryFn: ({ pageParam }) =>
      listJobs({
        project_id: selectedProject!.id,
        per_page: PER_PAGE,
        search: search.trim() || undefined,
        status: "active",
        // Empty string opts the backend into cursor mode for the first page.
        cursor: pageParam,
      }),
    initialPageParam: "",
    getNextPageParam: (lastPage) => lastPage.next_cursor || undefined,
    enabled: open && !!selectedProject?.id && !forbidden,
  });

  useEffect(() => {
    const err = jobsQuery.error as { status?: number } | null;
    if (err?.status === 403) setForbidden(true);
  }, [jobsQuery.error]);

  const viewportRef = useRef<HTMLDivElement>(null);
  useInfiniteScroll({
    rootRef: viewportRef,
    hasNextPage: jobsQuery.hasNextPage,
    isFetching: jobsQuery.isFetchingNextPage,
    onLoadMore: jobsQuery.fetchNextPage,
    enabled: open,
  });

  const jobs = jobsQuery.data?.pages.flatMap((p) => p.jobs) ?? [];
  const label = value ? value.title || "Unnamed" : null;

  return (
    <Select.Root
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (nextOpen) setSearch("");
      }}
    >
      <Select.Trigger
        variant={variant}
        className={className}
        active={!!value}
        {...triggerProps}
      >
        <Icon name="IconBag" size={14} className="shrink-0" />
        <Select.TriggerContent>{label || placeholder}</Select.TriggerContent>
        {value && value.id === lastJobId && (
          <span className="shrink-0 rounded border border-border bg-contrast/6 px-1.5 py-0.5 text-[11px] font-medium leading-none text-contrast/60">
            Last used
          </span>
        )}
        {!hideChevron && <Select.Icon className="ml-auto" />}
      </Select.Trigger>

      <Select.Content
        matchTriggerWidth={false}
        className="w-[380px] min-w-[320px]"
        align="start"
      >
        <Select.Search
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search jobs…"
          autoFocus
        />
        <Select.List viewportRef={viewportRef}>
          {forbidden ? (
            <div className="flex flex-col items-center justify-center gap-0.5 px-3 py-6">
              <p className="text-sm font-medium text-heading">No access</p>
              <p className="mt-0.5 text-center text-xs leading-normal text-contrast/80">
                You don&apos;t have permission to view jobs in this project.
              </p>
            </div>
          ) : jobsQuery.isLoading ? (
            <div className="flex items-center justify-center py-6">
              <Icon
                name="IconLoadingCircle"
                size={18}
                className="animate-spin text-contrast/40"
              />
            </div>
          ) : jobs.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-0.5 px-3 py-6">
              <p className="text-sm font-medium text-heading">No jobs found</p>
              <p className="mt-0.5 text-center text-xs leading-normal text-contrast/80">
                {search.trim()
                  ? "No matching jobs. Try a different search."
                  : "No active jobs in this project."}
              </p>
            </div>
          ) : (
            <>
              {jobs.map((job, i) => (
                <Select.Item
                  key={job.id}
                  index={i}
                  selected={job.id === value?.id}
                  onSelect={() => {
                    onChange(job);
                    setOpen(false);
                  }}
                >
                  <Select.ItemIcon>
                    <Icon
                      name="IconBag"
                      size={14}
                      className="text-heading opacity-60"
                    />
                  </Select.ItemIcon>
                  <Select.ItemLabel selected={job.id === value?.id}>
                    {job.title || "Unnamed"}
                  </Select.ItemLabel>
                  {job.id === lastJobId && job.id !== value?.id && (
                    <span className="shrink-0 rounded border border-border bg-contrast/6 px-1.5 py-0.5 text-[11px] font-medium leading-none text-contrast/60">
                      Last used
                    </span>
                  )}
                  {job.id === value?.id && <Select.ItemCheck />}
                </Select.Item>
              ))}
              {jobsQuery.isFetchingNextPage && (
                <div className="flex items-center justify-center py-2">
                  <Icon
                    name="IconLoadingCircle"
                    size={16}
                    className="animate-spin text-contrast/40"
                  />
                </div>
              )}
            </>
          )}
        </Select.List>
      </Select.Content>
    </Select.Root>
  );
}
